import { breakpoints, Button, Dialog, Heading, Inline, Stack } from '@etg/wings';
import { css } from '@eti/styles';
import { useState } from 'react';
import { OrderState, PartOfExternalFlights } from '@eti/schema-types';
import { useProperty, useTranslation } from '@eti/providers';
import { capitalize } from '../utils';
import RadioSelection from './common/RadioSelection';
import SelectBoxSelection from './common/SelectBoxSelection';
import InputSelection from './common/InputSelection';
import { DebugButton } from './index';

const fieldSetStyles = css`
  border: 1px solid #bbb;
  border-radius: 3px;
  padding: 56px 16px 16px;
  position: relative;
`;

const flexStyles = css`
  flex: 1;
`;

const legendStyles = css`
  position: absolute;
  top: 16px;
`;

const rebookEligibilityOptions = [
  {
    value: 'PRICE_REVIEW',
    label: 'Price Review',
  },
  {
    value: 'PRICE_REVIEW_FLEXIBLE_TICKET',
    label: 'Price Review Flexible Ticket',
  },
  {
    value: 'CHANGES_ALLOWED_CONTACT_ETG',
    label: 'Changes Allowed Contact ETG',
  },
  {
    value: 'CHECK_IF_CHANGES_ALLOWED_CONTACT_ETG',
    label: 'Check If Changes Allowed Contact ETG',
  },
  {
    value: 'FLEXIBLE_TICKET_CONTACT_ETG',
    label: 'Flexible Ticket Contact ETG',
  },
  {
    value: 'CHANGES_UNKNOWN_CONTACT_ETG',
    label: 'Changes Unknown Contact ETG',
  },
  {
    value: 'CONTACT_AIRLINE',
    label: 'Contact Airline',
  },
  {
    value: 'CONTACT_AIRLINE_SHORT_DEPARTURE',
    label: 'Contact Airline Short Departure',
  },
  {
    value: 'CONTACT_AIRLINE_ALREADY_EXCHANGED',
    label: 'Contact Airline Already Exchanged',
  },
  {
    value: 'NOT_CHANGEABLE',
    label: 'Not Changeable',
  },
  {
    value: 'NOT_FULLY_TICKETED',
    label: 'Not Fully Ticketed',
  },
];

const rebookEligibilityV1Options = [
  {
    value: 'change_trip_yourself',
    label: 'Change trip yourself',
  },
  {
    value: 'change_trip_yourself_no_price_review',
    label: 'Change trip yourself - No price review',
  },
  {
    value: 'change_trip_yourself_ancillaries',
    label: 'Change trip yourself - Ancillaries',
  },
  {
    value: 'change_trip_yourself_ancillaries_no_price_review',
    label: 'Change trip yourself - Ancillaries - No price review',
  },
  {
    value: 'change_trip_yourself_flexible_ticket_trip',
    label: 'Change trip yourself - Flexible ticket',
  },
  {
    value: 'change_flexible_ticket_trip',
    label: 'Change trip with us - Flexible ticket',
  },
  {
    value: 'change_trip_with_us',
    label: 'Change trip with us',
  },
  {
    value: 'change_trip_with_airline',
    label: 'Change trip with airline',
  },
  {
    value: 'change_not_available',
    label: 'Change not available',
  },
  {
    value: 'change_this_pnr_with_us',
    label: 'Change this pnr with us',
  },
  {
    value: 'change_short_departure_trip',
    label: 'Change short departure trip',
  },
  {
    value: 'change_not_confirmed_trip',
    label: 'Change not confirmed trip',
  },
  {
    value: 'change_exchanged_by_airline',
    label: 'Change exchanged by airline',
  },
  {
    value: 'change_not_open_ticket',
    label: 'Change not open ticket',
  },
  {
    value: 'change_trip_unknown',
    label: 'Change trip unknown',
  },
];

const orderStatusOptions = [
  {
    label: 'Initial',
    value: OrderState.Initial,
  },
  {
    label: 'Canceled',
    value: OrderState.Canceled,
  },
  {
    label: 'Changed',
    value: OrderState.Changed,
  },
];

const rebookingStatusOptions = [
  {
    label: 'Created',
    value: 'CREATED',
  },
  {
    label: 'In Progress',
    value: 'IN_PROGRESS',
  },
  {
    label: 'Complex Handling',
    value: 'COMPLEX_HANDLING',
  },
  {
    label: 'Failed',
    value: 'FAILED',
  },
  {
    label: 'Completed',
    value: 'COMPLETED',
  },
];

const partOfExternalFlightsOptions = [
  {
    label: 'None',
    value: PartOfExternalFlights.None,
  },
  {
    label: 'Some',
    value: PartOfExternalFlights.Some,
  },
  {
    label: 'All',
    value: PartOfExternalFlights.All,
  },
];

const partOfVivaAerobusFlightsOptions = [
  {
    label: 'None',
    value: 'NONE',
  },
  {
    label: 'Some',
    value: 'SOME',
  },
  {
    label: 'All',
    value: 'ALL',
  },
];

const cancelWithRefundOfferResponse = [
  {
    label: 'Success',
    value: 'SUCCESS',
  },
  {
    label: 'Error: Cancellation process failed',
    value: 'CANCELLATION_PROCESS_FAILED',
  },
  {
    label: 'Error: Order already cancelled',
    value: 'ORDER_ALREADY_CANCELLED',
  },
  {
    label: 'Error: Time expired',
    value: 'TIME_EXPIRED',
  },
];

const scheduleChangeScenario = [
  {
    label: 'No Schedule Change',
    value: 'NO_SCHEDULE_CHANGE',
  },
  {
    label: 'Schedule Change: 1st Bound, 1st Segment',
    value: 'BOUND_1_SEGMENT_1',
  },
  {
    label: 'Schedule Change: 1st Bound, ALL Segments',
    value: 'BOUND_1_SEGMENT_ALL',
  },
  {
    label: 'Schedule Change: ALL Bounds, 1st Segment',
    value: 'BOUND_ALL_SEGMENT_1',
  },
  {
    label: 'Schedule Change: ALL Bounds, ALL Segments',
    value: 'BOUND_ALL_SEGMENT_ALL',
  },
];

const priceLockOrderStateOptions = [
  { label: 'None', value: 'NONE' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Used', value: 'USED' },
  { label: 'Claimed', value: 'CLAIMED' },
  { label: 'Expired', value: 'EXPIRED' },
  { label: 'Refund', value: 'REFUND' },
  { label: 'Unknown', value: 'UNKNOWN' },
];

const protectedConnectionTierOptions = [
  { label: 'Off (property off, today)', value: 'OFF' },
  { label: 'On, no STG product (today, protected)', value: 'NO_ITEM' },
  { label: 'On, Standard tier (protected)', value: 'STG_STANDARD' },
  { label: 'On, No Guarantee tier (without protection)', value: 'STG_NO_GUARANTEE' },
  { label: 'On, STG product without a tier (can not tell)', value: 'NO_TIER' },
  {
    label: 'On, STG product without buy specification (can not tell)',
    value: 'NO_BUY_SPECIFICATION',
  },
];

const checkinStatuses = [
  {
    label: 'Pending',
    value: 'PENDING',
  },
  {
    label: 'Success',
    value: 'SUCCESS',
  },
  {
    label: 'Failed',
    value: 'FAILED',
  },
];

const DebugOrderDetailPage = () => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleSubmit = (event: any) => {
    const formData = new FormData(event.target);
    const formEntries = Array.from(formData.entries());

    for (const [key, value] of formEntries) {
      sessionStorage.setItem(`mocked${capitalize(key)}`, String(value));
    }
  };

  return (
    <>
      <DebugButton
        data-testid="order-details-properties-button"
        onClick={() => {
          setIsDialogOpen(true);
        }}
      >
        Order Details properties
      </DebugButton>
      <Dialog
        closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
        footer={
          <Inline align="end" collapseBelow={breakpoints._560}>
            <Button
              data-testid="order-details-properties-save-button"
              form="orderDetailsPropertiesConfigForm"
              type="submit"
              variant="primary"
            >
              Save
            </Button>
          </Inline>
        }
        isOpen={isDialogOpen}
        onDismiss={() => {
          setIsDialogOpen(false);
        }}
        title="Order Details properties"
      >
        <form
          action="/debug.orderdetailsproperties.mock.config"
          id="orderDetailsPropertiesConfigForm"
          method="POST"
          onSubmit={handleSubmit}
        >
          <Stack>
            <SelectBoxSelection
              data-testid="orderStatus-dropdown"
              defaultValue={orderStatusOptions[0].value}
              label="Order Status"
              name="orderStatus"
              options={orderStatusOptions}
              placeholder="Please select…"
            />
            <fieldset className={fieldSetStyles}>
              <Stack>
                <Heading as="legend" className={legendStyles} level={3}>
                  Rebooking
                </Heading>
                <RadioSelection
                  className={flexStyles}
                  defaultValue="no"
                  id="isRebookingEnabled"
                  label="Is Rebooking enabled?"
                />
                <SelectBoxSelection
                  data-testid="rebookingStatus-dropdown"
                  defaultValue={rebookingStatusOptions[0].value}
                  label="Rebooking Status"
                  name="rebookingStatus"
                  options={rebookingStatusOptions}
                  placeholder="Please select…"
                />
              </Stack>
            </fieldset>
            <fieldset className={fieldSetStyles}>
              <Stack>
                <Heading as="legend" className={legendStyles} level={3}>
                  Ryanair
                </Heading>
                <RadioSelection
                  className={flexStyles}
                  defaultValue="no"
                  id="hasManyPnrs"
                  label="Has many pnrs?"
                />
                <SelectBoxSelection
                  data-testid="partOfExternalFlights-dropdown"
                  defaultValue={partOfExternalFlightsOptions[0].value}
                  label="Part of external flights:"
                  name="partOfExternalFlights"
                  options={partOfExternalFlightsOptions}
                  placeholder="Please select…"
                />
                <Inline noWrap>
                  <RadioSelection
                    className={flexStyles}
                    defaultValue="no"
                    id="isEligibleForRefundRequest"
                    label="Is eligible for Refund Request?"
                  />
                  <InputSelection
                    className={flexStyles}
                    defaultValue="350000"
                    id="refundRequestExpirationTime"
                    label="Refund Request expiration time (in seconds)"
                  />
                </Inline>
              </Stack>
            </fieldset>
            <fieldset className={fieldSetStyles}>
              <Stack>
                <Heading as="legend" className={legendStyles} level={3}>
                  VivaAerobus
                </Heading>
                <SelectBoxSelection
                  data-testid="partOfVivaAerobusFlights-dropdown"
                  defaultValue={partOfVivaAerobusFlightsOptions[0].value}
                  label="Part of VivaAerobus flights:"
                  name="partOfVivaAerobusFlights"
                  options={partOfVivaAerobusFlightsOptions}
                  placeholder="Please select…"
                />
              </Stack>
            </fieldset>
            <fieldset className={fieldSetStyles}>
              <Stack>
                <Heading as="legend" className={legendStyles} level={3}>
                  Manage trip
                </Heading>
                <fieldset className={fieldSetStyles}>
                  <Stack>
                    <Heading as="legend" className={legendStyles} level={3}>
                      Schedule change
                    </Heading>
                    <SelectBoxSelection
                      data-testid="scheduleChangeScenario-dropdown"
                      defaultValue={scheduleChangeScenario[0].value}
                      label="Schedule Change Scenario"
                      name="scheduleChangeScenario"
                      options={scheduleChangeScenario}
                      placeholder="Set schedule change scenario:"
                    />
                    <Inline className={flexStyles} noWrap>
                      <InputSelection
                        defaultValue="30"
                        id="scheduleChangeInMinutes"
                        label="Schedule change (in minutes)"
                      />
                      <InputSelection
                        defaultValue="1"
                        id="scheduleChangeInDays"
                        label="Schedule change (in days)"
                      />
                    </Inline>
                  </Stack>
                </fieldset>
                {p('OrderDetails.RebookEligibility.V2.Enabled') ? (
                  <fieldset className={fieldSetStyles}>
                    <Stack>
                      <Heading as="legend" className={legendStyles} level={3}>
                        Rebook Eligibility
                      </Heading>
                      <SelectBoxSelection
                        data-testid="rebookEligibilityStatus-dropdown"
                        defaultValue={rebookEligibilityOptions[0].value}
                        label="Rebook Eligibility Status"
                        name="rebookEligibilityStatus"
                        options={rebookEligibilityOptions}
                        placeholder="Set rebook eligibility status:"
                      />
                    </Stack>
                  </fieldset>
                ) : (
                  <fieldset className={fieldSetStyles}>
                    <Stack>
                      <Heading as="legend" className={legendStyles} level={3}>
                        Rebook Eligibility V1
                      </Heading>
                      <SelectBoxSelection
                        data-testid="rebookEligibilityV1Status-dropdown"
                        defaultValue={rebookEligibilityV1Options[0].value}
                        label="Rebook Eligibility V1 Status"
                        name="rebookEligibilityV1Status"
                        options={rebookEligibilityV1Options}
                        placeholder="Set rebook eligibility status:"
                      />
                    </Stack>
                  </fieldset>
                )}
                <fieldset className={fieldSetStyles}>
                  <Stack>
                    <Heading as="legend" className={legendStyles} level={3}>
                      Cancellation properties
                    </Heading>
                    <SelectBoxSelection
                      data-testid="cancelWithRefundOfferResponse-dropdown"
                      defaultValue={cancelWithRefundOfferResponse[0].value}
                      label="Cancel with refund offer - Response"
                      name="cancelWithRefundOfferResponse"
                      options={cancelWithRefundOfferResponse}
                      placeholder="Set cancel with refund offer response:"
                    />
                    <Inline noWrap>
                      <RadioSelection
                        className={flexStyles}
                        defaultValue="no"
                        id="isEligibleForVoid"
                        label="Is eligible for Void?"
                      />
                      <InputSelection
                        className={flexStyles}
                        defaultValue="18000"
                        id="voidExpirationTime"
                        label="Void expiration time (in seconds)"
                      />
                    </Inline>
                    <Inline noWrap>
                      <RadioSelection
                        className={flexStyles}
                        defaultValue="no"
                        id="isEligibleForNonTicketed"
                        label="Is eligible for Non-ticketed (CXL before ticketing)?"
                      />
                      <InputSelection
                        className={flexStyles}
                        defaultValue="18000"
                        id="nonTicketedExpirationTime"
                        label="Non-ticketed expiration time, mock-only (UI ignores it)"
                      />
                    </Inline>
                    <Inline noWrap>
                      <RadioSelection
                        defaultValue="no"
                        id="isCancelForAnyReasonPurchased"
                        label="Is Cancel For Any Reason (CFAR) purchased?"
                      />
                    </Inline>
                    <Inline noWrap>
                      <RadioSelection
                        className={flexStyles}
                        defaultValue="no"
                        id="isEligibleForCancelForAnyReason"
                        label="Is eligible for Cancel For Any Reason?"
                      />
                      <InputSelection
                        className={flexStyles}
                        defaultValue="350000"
                        id="cfarExpirationTime"
                        label="CFAR expiration time (in seconds)"
                      />
                    </Inline>
                    <Inline noWrap>
                      <RadioSelection
                        className={flexStyles}
                        defaultValue="no"
                        id="isEligibleForTicketRules"
                        label="Is eligible for Ticket Rule cancellation?"
                      />
                      <InputSelection
                        className={flexStyles}
                        defaultValue="350000"
                        id="ticketRulesExpirationTime"
                        label="Ticket rules expiration time (in seconds)"
                      />
                    </Inline>
                    <Inline noWrap>
                      <RadioSelection
                        className={flexStyles}
                        defaultValue="no"
                        id="isEligibleForTwentyFourHourRule"
                        label="Is eligible for 24-Hour Rule cancellation?"
                      />
                      <InputSelection
                        className={flexStyles}
                        defaultValue="80000"
                        id="twentyFourHourRuleExpirationTime"
                        label="24-Hour rule expiration time (in seconds)"
                      />
                    </Inline>
                    <Inline noWrap>
                      <RadioSelection
                        className={flexStyles}
                        defaultValue="no"
                        id="isEligibleForTwentyFourHourBrRule"
                        label="Is eligible for 24-Hour (Brazil) Rule cancellation?"
                      />
                      <InputSelection
                        className={flexStyles}
                        defaultValue="80000"
                        id="twentyFourHourBrRuleExpirationTime"
                        label="24-Hour (Brazil) Rule expiration time (in seconds)"
                      />
                    </Inline>
                    <RadioSelection
                      defaultValue="no"
                      id="isCancellationGuaranteePurchased"
                      label="Is Cancellation Guarantee purchased?"
                    />
                    <RadioSelection
                      defaultValue="no"
                      id="isCancellationProtectionPurchased"
                      label="Is Cancellation Protection purchased?"
                    />
                  </Stack>
                </fieldset>
              </Stack>
            </fieldset>
            <fieldset className={fieldSetStyles}>
              <Stack>
                <Heading as="legend" className={legendStyles} level={3}>
                  Price Lock
                </Heading>
                <SelectBoxSelection
                  data-testid="priceLockOrderState-dropdown"
                  defaultValue={priceLockOrderStateOptions[0].value}
                  label="Price Lock order state"
                  name="priceLockOrderState"
                  options={priceLockOrderStateOptions}
                  placeholder="Please select…"
                />
              </Stack>
            </fieldset>
            <fieldset className={fieldSetStyles}>
              <Stack>
                <Heading as="legend" className={legendStyles} level={3}>
                  Protected Connection
                </Heading>
                <SelectBoxSelection
                  data-testid="protectedConnectionTier-dropdown"
                  defaultValue={protectedConnectionTierOptions[0].value}
                  label="Protected Connection (self-transfer orders)"
                  name="protectedConnectionTier"
                  options={protectedConnectionTierOptions}
                  placeholder="Please select…"
                />
              </Stack>
            </fieldset>
            <fieldset className={fieldSetStyles}>
              <Stack>
                <Heading as="legend" className={legendStyles} level={3}>
                  Check-in
                </Heading>
                <RadioSelection
                  className={flexStyles}
                  defaultValue="no"
                  id="isCheckInPurchased"
                  label="Is Check-in product purchased?"
                />
                <fieldset className={fieldSetStyles}>
                  <Stack>
                    <Heading as="legend" className={legendStyles} level={4}>
                      Purchased
                    </Heading>
                    <SelectBoxSelection
                      data-testid="check-in-statuses-dropdown"
                      defaultValue={checkinStatuses[0].value}
                      label="Purchased Check-in status:"
                      name="checkInStatus"
                      options={checkinStatuses}
                      placeholder="Please select…"
                    />
                    <RadioSelection
                      className={flexStyles}
                      defaultValue="yes"
                      id="isCheckInStatusSameForAllSegments"
                      label="Same for all segments"
                    />
                    <RadioSelection
                      className={flexStyles}
                      defaultValue="yes"
                      id="isCheckInStatusSameForAllPassengers"
                      label="Same for all passengers"
                    />
                  </Stack>
                </fieldset>
              </Stack>
            </fieldset>
            <fieldset className={fieldSetStyles}>
              <Stack>
                <Heading as="legend" className={legendStyles} level={3}>
                  Business receipt
                </Heading>
                <RadioSelection
                  className={flexStyles}
                  defaultValue="no"
                  id="isInvoiceAddressPersistenceEnabled"
                  label="Is invoice address persistence enabled?"
                />
                <RadioSelection
                  className={flexStyles}
                  defaultValue="no"
                  id="isInvoiceAddressPresent"
                  label="Has an invoice address present?"
                />
              </Stack>
            </fieldset>
          </Stack>
        </form>
      </Dialog>
    </>
  );
};

export default DebugOrderDetailPage;
